import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "../../api/httpClient";
import InfoCard from "../../components/card/infocard";
import StaffListModal from "../../components/company-settings/StaffListModal";
import {
  DeleteItem,
  InviteModal,
  SuccessfulDelete,
  SuccessModal,
} from "../../components/modal";
import DeleteImg from "../../assets/delLogo.png";
import verifyImg from "../../assets/verifyImg.png";
import { SimpleButton } from "../../components/button.jsx/Btn";
import { getApiBaseUrl } from "../../data/authStorage";
import FetchLoadingState from "../../components/feedback/FetchLoadingState";
import PageIntroNav from "../../components/navigation/PageIntroNav";
import { readSessionCache, writeSessionCache } from "../../utils/sessionCache";

const BranchViewDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const apiBaseUrl = getApiBaseUrl();
  const branchId = useMemo(
    () => location.state?.branchId || sessionStorage.getItem("selectedBranchId"),
    [location.state],
  );
  const cacheKey = useMemo(() => `cehr_branch_details_${branchId || "unknown"}`, [branchId]);
  const cachedData = useMemo(
    () => readSessionCache(cacheKey, { branch: null }),
    [cacheKey],
  );

  const [branch, setBranch] = useState(cachedData?.branch || null);
  const [isLoading, setIsLoading] = useState(!cachedData?.branch);
  const [errorMessage, setErrorMessage] = useState("");
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [openDeleteSuccessModal, setOpenDeleteSuccessModal] = useState(false);
  const [branchName, setBranchName] = useState("");
  const [branchAddress, setBranchAddress] = useState("");
  const [status, setStatus] = useState("Active");
  const [formErrorMessage, setFormErrorMessage] = useState("");
  const [deleteErrorMessage, setDeleteErrorMessage] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isStaffModalOpen, setIsStaffModalOpen] = useState(false);
  const [staffMembers, setStaffMembers] = useState([]);
  const [isStaffLoading, setIsStaffLoading] = useState(false);
  const [staffErrorMessage, setStaffErrorMessage] = useState("");

  const resolveStaffList = (response) => {
    const payload = response?.data || {};

    if (Array.isArray(payload.staff)) return payload.staff;
    if (Array.isArray(payload.employees)) return payload.employees;
    if (Array.isArray(payload.data)) return payload.data;

    return [];
  };

  const loadBranchStaff = useCallback(async () => {
    if (!apiBaseUrl || !branchId) {
      setStaffErrorMessage("Unable to load staff for this branch right now.");
      return;
    }

    try {
      setIsStaffLoading(true);
      setStaffErrorMessage("");

      const response = await axios.get(`${apiBaseUrl}/organizations/branches/${branchId}/staff`);
      setStaffMembers(resolveStaffList(response));
    } catch (error) {
      const responseMessage =
        error?.response?.data?.message || "Unable to load staff for this branch. Please try again.";
      setStaffErrorMessage(Array.isArray(responseMessage) ? responseMessage.join(", ") : responseMessage);
      setStaffMembers([]);
    } finally {
      setIsStaffLoading(false);
    }
  }, [apiBaseUrl, branchId]);

  const openStaffModal = () => {
    setIsStaffModalOpen(true);
    void loadBranchStaff();
  };

  const loadBranch = useCallback(async ({ silent = false } = {}) => {
      if (!apiBaseUrl || !branchId) {
        setErrorMessage(!branchId ? "Select a branch from the list first." : "API base URL is not configured.");
        return;
      }

      try {
        if (!silent) {
          setIsLoading(true);
        }
        setErrorMessage("");

        const response = await axios.get(`${apiBaseUrl}/organizations/branches/${branchId}`);
        const loadedBranch = response?.data?.branch || null;

        setBranch(loadedBranch);
        setBranchName(loadedBranch?.branchName || "");
        setBranchAddress(loadedBranch?.branchAddress || "");
        setStatus(loadedBranch?.status || "Active");
        writeSessionCache(cacheKey, { branch: loadedBranch });
      } catch (error) {
        const responseMessage =
          error?.response?.data?.message || "Unable to load branch details. Please try again.";
        setErrorMessage(Array.isArray(responseMessage) ? responseMessage.join(", ") : responseMessage);
      } finally {
        setIsLoading(false);
      }
    }, [apiBaseUrl, branchId, cacheKey]);

  useEffect(() => {
    loadBranch({ silent: true });
  }, [loadBranch]);

  useEffect(() => {
    const handleFocusRefresh = () => {
      if (document.visibilityState !== "hidden") {
        loadBranch({ silent: true });
      }
    };

    window.addEventListener("focus", handleFocusRefresh);
    document.addEventListener("visibilitychange", handleFocusRefresh);

    return () => {
      window.removeEventListener("focus", handleFocusRefresh);
      document.removeEventListener("visibilitychange", handleFocusRefresh);
    };
  }, [loadBranch]);

  const refreshBranch = async () => {
    if (!apiBaseUrl || !branchId) {
      return;
    }

    const response = await axios.get(`${apiBaseUrl}/organizations/branches/${branchId}`);
    const loadedBranch = response?.data?.branch || null;

    setBranch(loadedBranch);
    setBranchName(loadedBranch?.branchName || "");
    setBranchAddress(loadedBranch?.branchAddress || "");
    setStatus(loadedBranch?.status || "Active");
  };

  const handleOpenEditModal = () => {
    setFormErrorMessage("");
    setOpenEditModal(true);
  };

  const openDeleteBranch = () => {
    setDeleteErrorMessage("");
    setOpenDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setOpenDeleteModal(false);
    setDeleteErrorMessage("");
  };

  const handleUpdateBranch = async (ev) => {
    ev.preventDefault();

    if (!branchId || !apiBaseUrl) {
      setFormErrorMessage("Unable to update this branch right now.");
      return;
    }

    if (!branchName.trim() || !branchAddress.trim() || !status.trim()) {
      setFormErrorMessage("Branch name, branch address and status are required.");
      return;
    }

    try {
      setIsSaving(true);
      setFormErrorMessage("");

      await axios.patch(`${apiBaseUrl}/organizations/branches/${branchId}`, {
        branchName: branchName.trim(),
        branchAddress: branchAddress.trim(),
        status: status.trim(),
      });

      await refreshBranch();
      setOpenEditModal(false);
      setOpenSuccessModal(true);
    } catch (error) {
      const responseMessage =
        error?.response?.data?.message || "Unable to update branch. Please try again.";
      setFormErrorMessage(Array.isArray(responseMessage) ? responseMessage.join(", ") : responseMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteBranch = async () => {
    if (!branchId || !apiBaseUrl) {
      setDeleteErrorMessage("Unable to delete this branch right now.");
      return;
    }

    try {
      setIsDeleting(true);
      setDeleteErrorMessage("");

      await axios.delete(`${apiBaseUrl}/organizations/branches/${branchId}`);

      setOpenDeleteModal(false);
      setOpenDeleteSuccessModal(true);
      sessionStorage.removeItem("selectedBranchId");

      setTimeout(() => {
        navigate("/company-settings/branches");
      }, 1200);
    } catch (error) {
      const responseMessage =
        error?.response?.data?.message || "Unable to delete branch. Please try again.";
      setDeleteErrorMessage(Array.isArray(responseMessage) ? responseMessage.join(", ") : responseMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  const title = branch?.branchName || "Branch";
  const descriptionText = branch?.branchAddress || "No branch address available yet.";
  const createdAt = branch?.createdAt
    ? new Date(branch.createdAt).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "-";

  return (
    <div>
      <PageIntroNav
        breadcrumbs={[
          { label: "Company Settings", path: "/company-settings" },
          { label: "Branches", path: "/company-settings/branches" },
          { label: title },
        ]}
        title={title}
        subtitle="Branch details and management actions."
        fallbackPath="/company-settings/branches"
        backLabel="Back to Branches"
        actions={(
          <div className="flex items-center gap-3">
            <button
              onClick={handleOpenEditModal}
              className="border border-[#1234CD] text-[#1234CD] px-4 py-2 rounded-xl"
            >
              Edit
            </button>
            <button
              onClick={openDeleteBranch}
              className="border border-red-500 text-red-600 px-4 py-2 rounded-xl"
            >
              Delete
            </button>
          </div>
        )}
      />

      {isLoading && !branch ? (
        <FetchLoadingState message="Loading branch details..." className="min-h-[60vh]" />
      ) : null}

      {errorMessage && !branch ? (
        <div className="px-6 py-4 text-sm text-red-600" role="alert">
          {errorMessage}
        </div>
      ) : null}

      {!isLoading && branch ? (
        <InfoCard
          details="Branch Details"
          title={title}
          description={descriptionText}
          metaData={[
            { label: "Date created", value: createdAt },
            { label: "Status", value: branch?.status || "-" },
            { label: "Total staff", value: branch?.totalStaff ?? 0 },
            { label: "Branch ID", value: branch?.id || "-" },
          ]}
        />
      ) : null}

      {!isLoading && branch ? (
        <div className="px-6 mt-6 flex flex-wrap gap-3">
          <button
            type="button"
            onClick={openStaffModal}
            className="px-4 py-2 rounded-xl bg-[#1234CD] text-white font-medium"
          >
            View Branch Staff
          </button>
        </div>
      ) : null}

      <DeleteItem
        isOpen={openDeleteModal}
        onClose={closeDeleteModal}
      >
        <div>
          <img
            className="w-40 mx-auto animate-pulse"
            src={DeleteImg}
            alt="delete branch"
          />

          <h1 className="text-lg text-center text-[#000000] font-bold">
            Continue to delete this branch?
          </h1>
          <p className="text-sm text-center mt-2">Action cannot be undone.</p>
          {deleteErrorMessage && (
            <p className="text-sm text-center mt-3 text-red-600" role="alert">
              {deleteErrorMessage}
            </p>
          )}
        </div>
        <div className="flex items-center py-4 gap-4 my-4">
          <button
            onClick={closeDeleteModal}
            className="border-[#1233CC] py-1 border-2 text-[#1233CC] font-medium px-4 rounded-[10px] w-[102px]"
          >
            No
          </button>
          <button
            className="text-[16px] font-medium px-4 py-1 bg-[#1233CC] rounded-[10px] w-[102px] text-white disabled:opacity-60 disabled:cursor-not-allowed"
            onClick={handleDeleteBranch}
            disabled={isDeleting}
          >
            {isDeleting ? "Deleting..." : "Yes"}
          </button>
        </div>
      </DeleteItem>

      <SuccessfulDelete
        isOpen={openDeleteSuccessModal}
        onClose={() => setOpenDeleteSuccessModal(false)}
      >
        <div>
          <img
            src={verifyImg}
            alt="branch deleted"
            className="w-20 animate-ping mx-auto"
          />
          <div className="text-center mt-6">
            <h2 className="text-black text-[20px] leading-[27px] font-bold mb-2">
              Branch deleted
            </h2>
            <p className="text-[#74777B] text-[14px] leading-[18.9px]">
              The branch was removed successfully.
            </p>
          </div>
        </div>
      </SuccessfulDelete>

      <InviteModal
        isOpen={openEditModal}
        onClose={() => setOpenEditModal(false)}
      >
        <div className="my-8">
          <div>
            <p className="text-2xl font-semibold text-black font-Outfit">Edit Branch</p>
          </div>
          <form
            className="my-12"
            onSubmit={handleUpdateBranch}
          >
            {formErrorMessage && (
              <p className="mb-4 text-sm text-red-600" role="alert">
                {formErrorMessage}
              </p>
            )}

            <div>
              <label
                htmlFor="branch-name"
                className="block mb-2 font-[500] text-[14px] text-black leading-[18.9px] font-satosh"
              >
                Branch Name
              </label>
              <input
                type="text"
                id="branch-name"
                value={branchName}
                onChange={(ev) => setBranchName(ev.target.value)}
                className="w-full p-2 text-center mb-2 rounded-xl focus:outline-[#1234CD] border-2 border-[#F3F5F5] py-[16px]"
              />
            </div>

            <div>
              <label
                htmlFor="branch-address"
                className="block mb-2 font-[500] text-[14px] text-black leading-[18.9px] font-satosh"
              >
                Branch Address
              </label>
              <input
                type="text"
                id="branch-address"
                value={branchAddress}
                onChange={(ev) => setBranchAddress(ev.target.value)}
                className="w-full p-2 min-h-24 mb-4 text-center rounded-xl focus:outline-[#1234CD] border-2 border-[#F3F5F5] py-[16px]"
              />
            </div>

            <div>
              <label
                htmlFor="branch-status"
                className="block mb-2 font-[500] text-[14px] text-black leading-[18.9px] font-satosh"
              >
                Status
              </label>
              <select
                id="branch-status"
                value={status}
                onChange={(ev) => setStatus(ev.target.value)}
                className="w-full p-2 text-center mb-2 rounded-xl focus:outline-[#1234CD] border-2 border-[#F3F5F5] py-[16px]"
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>

            <div className="flex justify-center gap-12 mt-20 items-end">
              <SimpleButton
                onClick={() => setOpenEditModal(false)}
                type="button"
                className="font-[700] font-satoshi leading-[21.6px] flex justify-center items-center border w-40 rounded-xl py-[16px] border-[#1234CD] text-[#1234CD] text-[16px]"
              >
                Cancel
              </SimpleButton>

              <SimpleButton
                type="submit"
                className="font-[700] font-satoshi leading-[21.6px] flex justify-center items-center w-40 rounded-xl text-white py-[16px] bg-[#1234CD] text-[16px] disabled:opacity-60 disabled:cursor-not-allowed"
                disabled={isSaving}
              >
                <span>{isSaving ? "Updating..." : "Update"}</span>
              </SimpleButton>
            </div>
          </form>
        </div>
      </InviteModal>

      <SuccessModal
        isOpen={openSuccessModal}
        onClose={() => setOpenSuccessModal(false)}
      />

      <StaffListModal
        isOpen={isStaffModalOpen}
        onClose={() => setIsStaffModalOpen(false)}
        title={`${title} Staff`}
        subtitle="Staff assigned to this branch."
        staff={staffMembers}
        loading={isStaffLoading}
        error={staffErrorMessage}
        onRefresh={loadBranchStaff}
      />
    </div>
  );
};

export default BranchViewDetails;
